import React, { Component } from 'react';
import { getFormValues, SubmissionError } from 'redux-form';
import { withRouter } from 'react-router';
import { subscribe } from 'react-contextual';
import autobind from 'autobind-decorator';
import * as _ from 'lodash';
import classNames from 'classnames';
import { __, getLocalized, mapStateToProps, submitForm, validateForm } from '../core/utils';
import Localization from '../helpers/Localization';
import AddTaskIcon from '../assets/images/add-task-60x60.svg';
import AddTaskForm from '../forms/add_task_wizard/AddTaskForm';
import SelectPlanningSubstageForm from '../forms/add_task_wizard/SelectPlanningSubstageForm';
import {
  createTask,
  listPossibleTaskAssignee,
  listPossibleTaskFollowers,
  listPossibleSubstagesWithChildrenByStage,
} from '../redux/actions/table/all_tasks';
import { listPossibleStages } from '../redux/actions/table/substages';
import {
  getStageDefaultVisibility,
  getStagesLanguages,
  listProjectSubstages,
} from '../redux/actions/table/stages';
import { listPossibleStageDisciplines } from '../redux/actions/table/teams';
import { listPossibleTags } from '../redux/actions/general';
import { setNotification } from '../redux/actions/general/notification';
import styles from '../styles/wizards/AddTimesheetWizard.scss';

@withRouter
@subscribe(Localization, 'localization')
@mapStateToProps((state) => ({
  languageId: state.auth.language_id,
  selection: getFormValues('add_task_from_project_planning.select_substage')(state),
}))
class AddTaskFromProjectPlanningWizard extends Component {
  state = { step: 1, stages: [], options: [], loading: true, loadingSubstages: false };

  componentDidMount() {
    this.mounted = true;
    listPossibleStages(this.props.match.params.project).then(({ response }) => {
      if (this.mounted) {
        this.setState({
          stages: response.data.map((stage) => ({
            ...stage,
            label: getLocalized(stage.label, this.props.languageId),
          })),
          loading: false,
        });
      }
    }, () => {
      if (this.mounted) this.setState({ loading: false });
    });
  }

  @autobind
  _selectStage(stageId) {
    this.selectedStageId = stageId;
    this.setState({ options: [], loadingSubstages: !!stageId });
    if (!stageId) return;
    listPossibleSubstagesWithChildrenByStage(stageId).then(({ response }) => {
      if (this.mounted && this.selectedStageId === stageId) {
        this.setState({
          options: _.uniqBy(
            response.data.filter((item) => Number(item.group_id) > 0),
            'group_id',
          ).map((substage) => ({
            value: substage.group_id,
            stageId,
            label: getLocalized(substage.group, this.props.languageId),
          })),
          loadingSubstages: false,
        });
      }
    }, () => {
      if (this.mounted && this.selectedStageId === stageId) {
        this.setState({ loadingSubstages: false });
      }
    });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  _peopleOptions(items) {
    const { disciplines } = this.props.localization;
    return items.map((item) => item.type === 'discipline' ? {
      ...item,
      name: [item.company_name, [
        item.stage_discipline.code,
        item.stage_discipline.discipline_id == null
          ? item.stage_discipline.description + '*'
          : _.get(disciplines, [item.stage_discipline.discipline_id, 'name']),
      ].join(': ')].join(' | '),
    } : item);
  }

  @autobind
  _selectSubstage(values) {
    const errors = {};
    if (!values.stage_id) errors.stage_id = ['Field required'];
    if (!values.substage_id) errors.substage_id = ['Field required'];
    if (!_.isEmpty(errors)) throw new SubmissionError(errors);

    const target = this.state.options.find((item) => item.value == values.substage_id && item.stageId == values.stage_id);
    if (!target) throw new SubmissionError({ substage_id: ['Field required'] });

    return validateForm(Promise.all([
      listPossibleTaskAssignee(target.stageId),
      listPossibleTaskFollowers(target.stageId),
      getStageDefaultVisibility(target.stageId),
      getStagesLanguages(target.stageId),
      listPossibleStageDisciplines(target.stageId),
      listPossibleTags('project', this.props.match.params.project),
    ]).then(([assignee, followers, visibility, languages, disciplines, tags]) => {
      if (!this.mounted) return;
      this.setState({
        step: 2,
        target,
        assignee: this._peopleOptions(assignee.response.data),
        followers: this._peopleOptions(followers.response.data),
        defaultVisibility: _.toString(visibility.response.data.default_visibility),
        projectLanguages: languages.response.data,
        stageDisciplines: disciplines.response.data,
        stageTags: tags.response.data,
      });
    }));
  }

  @autobind
  _handleSubmit(values) {
    const { target } = this.state;
    if (!target) return;

    const data = {
      ..._.omit(values, ['edit']),
      type: 'group',
      substage_id: target.value,
      assignee: _.map(values.assignee, 'id'),
      followers: _.map(values.followers, 'id'),
    };

    return validateForm(createTask(target.stageId, data).then(() => {
      listProjectSubstages(this.props.match.params.project);
      setNotification(__('wizard.add-task.confirm'));
      this.props.onClose();
    }));
  }

  render() {
    const { step, stages, options, loading, loadingSubstages, target } = this.state;
    const steps = [
      { id: 1, title: [__('add-timesheet-form.stage'), __('wizard.add-task.substage')].join(' / ') },
      { id: 2, title: __('wizard.add-task.title.create-new') },
    ];
    return (
      <>
        <div className={styles.header}>
          <div className={styles.title}>
            <div className={styles.left}>
              <span>{__('wizard.add-task.name.create-new-task')}</span>
              <span>{__('wizard.add-task.subtitle.create-new')}</span>
            </div>
            <div className={styles.right}><AddTaskIcon /></div>
          </div>
          <div className={styles.steps}>
            {steps.map((item) => (
              <div
                key={item.id}
                className={classNames(styles.step, step === item.id && styles.active)}
                onClick={() => {
                  if (item.id === step) return;
                  if (item.id === 1) this.setState({ step: 1 });
                  else submitForm('add_task_from_project_planning.select_substage');
                }}
              >
                <span>{item.id === 1 ? '01' : '02'}</span>
                <span>{item.title}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.body}>
          {step === 1 ? (
            <SelectPlanningSubstageForm
              stages={stages}
              options={options}
              loading={loading}
              loadingSubstages={loadingSubstages}
              selectedStageId={this.props.selection?.stage_id}
              selectedSubstageId={this.props.selection?.substage_id}
              onStageChange={this._selectStage}
              initialValues={{ stage_id: target?.stageId, substage_id: target?.value }}
              onSubmit={this._selectSubstage}
              onClose={this.props.onClose}
            />
          ) : (
            <AddTaskForm
              stageId={target.stageId}
              lockTaskType
              onCancel={this.props.onClose}
              onBack={() => this.setState({ step: 1 })}
              possibleTaskAssignee={this.state.assignee}
              possibleTaskFollowers={this.state.followers}
              possibleStageDisciplines={this.state.stageDisciplines}
              stageTags={this.state.stageTags}
              projectLanguages={this.state.projectLanguages}
              onSubmit={this._handleSubmit}
              initialValues={{
                type: 'group',
                status: 'not-started',
                priority: 'normal',
                visibility: this.state.defaultVisibility,
                description: {},
                substage_id: target.value,
              }}
            />
          )}
        </div>
      </>
    );
  }
}

export default AddTaskFromProjectPlanningWizard;
